import {Component, forwardRef, Input, OnDestroy, OnInit, ChangeDetectorRef, EventEmitter, Output, AfterViewInit } from '@angular/core';
import {ControlValueAccessor, UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, NG_VALIDATORS, NG_VALUE_ACCESSOR} from '@angular/forms';
import {Subscription} from 'rxjs';
import {TranslateService} from '@ngx-translate/core';;
import { Store } from '@ngrx/store';
import { SourceSystem } from '@signal/asp-data-commons';
import { VehiclesState } from '../../../store/vehicle/vehicle.reducer';
import * as fromInvVehicleActions from '../../../store/vehicle/vehicle.actions';
import * as fromInvVehicleSelectors from '../../../store/vehicle/vehicle.selectors';
import _ from 'lodash';
import { SubscriptionList } from 'src/app/modules/shared/models/asp.types';
import { DealerState } from '../../../store/dealer/dealer.reducer';
import { selectDealerAddressLong, selectDealerCode } from '../../../store/dealer/dealer.selectors';
import { environment } from '../../../shared/services/util.service';
import { Brand } from '@signal/asp-data-commons';

@Component({
  selector: 'app-vehicle-configurator',
  templateUrl: './vehicle-configurator.component.html',
  styleUrls: ['./vehicle-configurator.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => VehicleConfiguratorComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => VehicleConfiguratorComponent),
      multi: true
    }
  ]
})
export class VehicleConfiguratorComponent implements OnInit, OnDestroy, AfterViewInit, ControlValueAccessor {
  @Input() isEditMode;
  @Input() vin;
  @Input() withoutFleetVehicle;
  @Input() appt;
  @Input() searchWithVin;
  @Output() formEdited:EventEmitter<boolean> = new EventEmitter();
  @Output() sourceChanged:EventEmitter<any> = new EventEmitter();

  vehicle: UntypedFormGroup;
  otherVehicles: UntypedFormArray;
  subscription: Subscription;
  disabled: boolean;
  modelList$: any;
  vehicleList$: any;
  vehicleFromList$: any;
  vehicleListColor$: any;
  countryCode: string = 'USA';
  anyTrimMarketingTitle = "anytrim$%$marketingTitle";
  isUpdated = false;
  vehicleNotFoundReset: boolean = false;
  fullData: any = {};
  sources: string[] = [];
  makeList: string[] = [];
  modelList: any[] = [];
  yearList: any[] = [];
  trimList: any[] = [];
  oldValue: any = {};
  load = false;
  isLexus = environment.brand === Brand.LEXUS.toLocaleLowerCase() ;
  allVehiclesCount=0;
  sourceOther='serviceSchedule.others';
  sourceNew='serviceSchedule.new';
  sourceUsed= this.isLexus ? 'otherTexts.preOwned' : 'serviceSchedule.used';
  sourceMap:any = {
    [this.translate.instant(this.sourceOther)] : "Others",
    [this.translate.instant(this.sourceNew)] : SourceSystem.NVI,
    [this.translate.instant(this.sourceUsed)] : SourceSystem.UVI,
    "CRM" : SourceSystem.CRM
  }
  otherSourceSubs: SubscriptionList = {};
  makes=["TOYOTA","LEXUS","SCION"];

  constructor(private readonly formBuilder: UntypedFormBuilder,
    private cdr: ChangeDetectorRef,
    private readonly vehiclesState: Store<VehiclesState>,
    private readonly translate: TranslateService,
    private readonly dealerState: Store<DealerState>,
    ) {
    this.vehicle = this.formBuilder.group({
      year: [''],
      model: [''],
      vin:[''],
      imageUrl:[''],
      make:[''],
      description:[''],
      source:[''],
      unitId:[''],
      dealerCode:['']
    });
    this.otherSourceSubs.dealerCode = this.dealerState.select(selectDealerCode).subscribe(dealerCode=> {
      if(dealerCode.startsWith('6')){
        this.makes=["LEXUS","TOYOTA","SCION"];
      }
    });
    this.otherSourceSubs.countryCode = this.dealerState.select(selectDealerAddressLong).subscribe(addressInfo => {
      if (addressInfo?.country) {
        this.countryCode = addressInfo?.country;
        this.populateDropdown();
      }
    });
    this.subscription = this.vehicle.valueChanges.subscribe(value => {
      const tempData: any = {year: null,
        model: null,
        vin: null,
        imageUrl: null,
        make: null,
        description: null,
        source : null,
        unitId: null,
        dealerCode: null};
      if(!this.oldValue?.source && value?.source && !value?.make && !value?.model && this.searchWithVin){
        this.searchWithVin = false;
        tempData.source = value.source;
        this.sourceChanged.emit({data:tempData,type:"sourceChanged"});
      } 
     
      if(this.searchWithVin){
        this.makeList = []
        this.modelList = []
        this.yearList = []
        this.fullData = {}
        return
      }
      if(_.isEqual(this.oldValue, value)) {
        return;
      }
      this.onChange(value);
      this.onTouched();

      if(!_.isEqual(this.oldValue, value)){
        this.handleChangesIfNotEqual(tempData,value)
        
      }
      this.oldValue = value;
      tempData.source = value.source
      if(tempData?.model?.length && !tempData?.year){
        this.vehicle.patchValue({year: tempData.year})
      }
      this.sourceChanged.emit({data:tempData,type:"Changes"});
      this.formEdited.emit(!this.vehicle.dirty);
    });
    
  }
  ngAfterViewInit(): void {
    this.load = true;
    this.vehiclesState.dispatch(new fromInvVehicleActions.FetchVehiclesBasedOnSource({source:this.sourceMap[this.translate.instant(this.sourceOther)]}))
    this.otherSourceSubs.vehicleMakeLoading = this.vehiclesState.select(fromInvVehicleSelectors.selectLoading).subscribe(loading =>{
      if(!loading){
        this.otherSourceSubs.vehicleMake = this.vehiclesState.select(fromInvVehicleSelectors.selectVehiclesMake)
        .subscribe(data =>{
          if(!data.allVehiclesCount) {
            this.sources = this.sources.filter((source) => source !== this.translate.instant(this.sourceOther) );
          }
        })
        this.load = false;
        this.otherSourceSubs.vehicleMake.unsubscribe();
        this.otherSourceSubs.vehicleMakeLoading.unsubscribe();
      }
    });
    this.cdr.detectChanges();
  }

  populateDropdown(){
    this.sources = Object.values(SourceSystem).map(s => {
      switch(s){
        case SourceSystem.ME:
          return this.translate.instant(this.sourceOther);
        case SourceSystem.NVI:
          return this.translate.instant(this.sourceNew)
        case SourceSystem.UVI:
          return this.translate.instant(this.sourceUsed)
        default:
          return s
      }
    });
    if(this.countryCode !== 'USA'){
      this.sources = this.sources.filter(s => s === SourceSystem.CRM).sort((a,b) => a.localeCompare(b))
    }else{
      this.sources = this.sources.filter(s => s !== SourceSystem.CRM).sort((a,b) => a.localeCompare(b))
    }
  }

  handleChangesIfNotEqual(tempData,value){
    if(value.source && this.oldValue.source !== value.source){
      this.handleSourceChange(tempData,value)
      return
    }
    if(value.make && value.make.length && this.oldValue.make !== value.make){
      this.handleMakeChange(tempData,value)
    }
    if(value.model && value.model.length && this.oldValue.model !== value.model){
      this.handleModelChange(tempData,value)
    }
    if(value.year && this.oldValue.year !== value.year){
      this.handleYearChange(tempData,value)
    }
  }

  handleYearChange(tempData,value){
    const vinList = this.fullData.modelList[value.make]?.filter(v => v.model === value.model && v.year === value.year)?.map(v => v?.vin)
    const str = vinList?.join(',')
    tempData.source = value.source
    tempData.make = value.make
    tempData.model = value.model
    tempData.description = value.description
    tempData.year = value.year;
    tempData.vin = str;
    this.sourceChanged.emit({data:tempData,type:"Changes"});
  }

  handleModelChange(tempData,value){
    this.yearList = []
    const filteredData = this.fullData.modelList[value.make]?.filter(v => v.model === value.model)?.map(v => v.year)?.sort()
    this.yearList = [...new Set(filteredData)]
    tempData.source = value.source
    tempData.make = value.make
    tempData.model = value.model
    tempData.year = null;
    this.sourceChanged.emit({data:tempData,type:"Changes"});
  }

  handleMakeChange(tempData,value){
    this.modelList = []
    this.yearList = []
    const filteredData = this.fullData.modelList[value.make]?.map(v => v.model)?.sort()
    this.modelList = [...new Set(filteredData)]
    tempData.source = value.source;
    tempData.make = value.make;
    this.sourceChanged.emit({data:tempData,type:"Changes"});
  }

  handleSourceChange(tempData,value){
    this.load = true;
    this.makeList = []
    this.modelList = []
    this.yearList = []
    tempData.source = value.source
    this.oldValue = tempData;
    /* As we show Toyota,Lexus on top,change in make wont be triggered as previous values could be same ,so set them to "" on changing source*/
    this.oldValue.make="";
    value.make="";
    this.vehicle.patchValue({...tempData})
    this.sourceChanged.emit({data:tempData,type:"Changes"});
    this.vehiclesState.dispatch(new fromInvVehicleActions.FetchVehiclesBasedOnSource({source:this.sourceMap[value.source]}))
    this.vehiclesState.select(fromInvVehicleSelectors.selectVehiclesMake)
    .subscribe(data =>{
      this.fullData = data;
      if(this.sourceMap[value.source] === SourceSystem.UVI && data?.makeList?.length)
      {
        this.makeList = this.arrangeMakes(data.makeList); 
      }
      else{
        this.makeList = data?.makeList?.sort();
      }
      this.sourceChanged.emit({data:[],type:"loaded"});
    });
    this.vehiclesState.select(fromInvVehicleSelectors.selectLoading).subscribe(loading =>{
      if(!loading){
        this.load = false;
      }
    })
  }

  ngOnInit(): void {
  }

  ngOnDestroy() {
    // this.subscription.unsubscribe();
    this.cdr.detach();
  }

  /* Put Toyota,Lexus,Scion on the top of the make list and arrange remaining alphabetically*/
  arrangeMakes(makeList) {
    const makes=[];
    const mkList=[...makeList];
    this.makes.forEach(make => {
      const index = mkList.findIndex(m => m.toUpperCase() === make);
      if (index !== -1) {
        makes.push(make);
        mkList.splice(index, 1)
      }
    });
    return makes.concat(mkList.sort((a,b)=> a - b));
  }

  get f() { 
    return this.vehicle.controls; 
  }

  

  compareWith(o1: any, o2: any) {
    return o1 === o2;
  }

  get value(): any {
    return this.vehicle.value;
  }

  set value(value: any) {
    if (this.disabled) { return; }
    if(!value.year && !value.make) { return; }
    this.vehicle.setValue(value);
    this.onChange(value);
    this.onTouched();
  }

  onChange: any = (value: any) => { };
  onTouched: any = () => { };

  registerOnChange(fn) {  
    this.onChange = fn;
  }

  writeValue(value) {
    if (value) {
      this.value = value;
    }

    if (value === null) {
      this.vehicle.reset();
    }
  }

  registerOnTouched(fn) {
    this.onTouched = fn;
  }
  
  // communicate the inner form validation to the parent form
  validate(_: UntypedFormControl) {
    return this.vehicle.valid ? null : { vehicle: { valid: false } };
  }

}
